/**
 * @file Mode Main (pleinement jouable) : chemin en oblique, chaque niveau = une quantite FIXE d'arbres a abattre (aucune repousse)
 * en 5 min, niveau boss tous les 10, les chats avancent de niveau en niveau, biomes tournants.
 */
import { Decimal } from '@shared/bignum';
import { okVoid } from '@shared/types';
import type { Result, Vec2 } from '@shared/types';
import { BIOME_ORDER } from '@config/biomes';
import type { ModeDef, SpawnEntry } from '@config/types';
import type { SimContext } from '../../Context';
import { biomeForStageValue, isBossLevel, levelSeconds, levelTreeCount, stageValueToDisplay } from '../../balance/formulas';
import { spawnTarget } from '../../ecs/EntityFactory';
import { BaseMode } from '../BaseMode';
import type { ModeCommand, ModeHud, ModeStartParams } from '../ModeTypes';
import { pickSpawnEntry } from '../spawnLayout';

// ---- Chemin (arene 'corridor' le long de +y) : les arbres d'un niveau forment des rangees DEVANT les chats.
/** Distance entre le debut du troncon (chats) et la 1re rangee d'arbres. */
const ROW_START = 9;
/** Ecart entre deux rangees. */
const ROW_GAP = 2.2;
/** Arbres par rangee (le dernier rang est centre). */
const PER_ROW = 6;
/** Marge laterale entre les arbres et les falaises. */
const SIDE_MARGIN = 0.7;
const ROW_JITTER_X = 0.35;
const ROW_JITTER_Y = 0.4;
/** Le boss se tient derriere les rangees. */
const BOSS_GAP = 2.8;
/** Recentrage periodique du chemin (precision flottante) : decalage multiple de la periode visuelle du sol (24 u). */
const REBASE_AT = 960;
const REBASE_STEP = 48;

/**
 * Regles (comme l'original) :
 *  - Le niveau courant est ctx.data.run.stageValue (0-based). Affichage "stage-niveau" via stageValueToDisplay.
 *  - Un niveau = levelTreeCount() arbres poses en rangees devant les chats (table du biome, poids des arbres rares x stat
 *    rareTreeChance), de plus en plus nombreux du niveau 1 au niveau 10 (plafond stage.treeCount.max) ; au niveau boss
 *    (isBossLevel) : ces arbres + 1 'tree_boss' (hpMult/rewardMult de boss) derriere les rangees.
 *    Les arbres abattus NE REPOUSSENT PAS : le niveau est gagne quand tout est abattu. Chrono = levelSeconds() (5 min).
 *  - Chrono ecoule : LevelFailed ("Temps ecoule !"), stageValue -= params.failFallbackLevels (min 0), puis le niveau recommence
 *    avec une nouvelle serie fixe d'arbres devant les chats (pas de phase de repousse infinie).
 *  - Victoire : gemmes (diaPerLevelClear / diaPerBossClear, x stat diaGain), LevelCleared, missions 'stageClears', stageValue++,
 *    records, StageChanged (biomeChanged si le biome change) ; les chats avancent jusqu'au bout de la bande abattue, ou commence
 *    le niveau suivant (intro puis nouvelles rangees).
 *  - Les recompenses d'argent par arbre sont gerees par combat/damage.killTarget (killRewards = 'money').
 */
export class MainMode extends BaseMode<'main'> {
  /** Debut (y) du troncon de chemin du niveau courant : point d'attente des chats. */
  private pathY = 0;
  /** Fin (y) de la bande d'arbres du niveau : les chats avancent jusque-la apres la victoire. */
  private bandEndY = 0;

  constructor(def: ModeDef<'main'>) {
    super(def);
  }

  protected override onEnter(ctx: SimContext, _params: ModeStartParams): void {
    const run = ctx.data.run;
    ctx.rt.biome = biomeForStageValue(ctx.config.balance, run.stageValue, BIOME_ORDER);
    // Ancien format (phase 'farm' apres un chrono ecoule) : le Main n'a plus qu'une seule phase.
    run.mainPhase = 'challenge';
    this.pathY = 0;
    this.bandEndY = 0;
    this.resetTimer(ctx);
    ctx.services.team.placeFormation(0, this.pathY);
  }

  private resetTimer(ctx: SimContext): void {
    this.timeTotal = levelSeconds(ctx.config.balance, ctx.data.run.stageValue);
    this.timeLeft = this.timeTotal;
  }

  /** AutoMove sans cible : les chats rejoignent le debut du troncon courant (ils avancent apres chaque niveau). */
  homePoint(): { x: number; y: number } {
    return { x: 0, y: this.pathY };
  }

  /** Rangees d'arbres devant les chats (rangs alternes decales, leger jitter). */
  private layoutRows(ctx: SimContext, count: number): Vec2[] {
    const halfW = this.def.rules.arena.halfW - SIDE_MARGIN;
    const cell = (2 * halfW) / PER_ROW;
    const rng = ctx.rng.spawn;
    const out: Vec2[] = [];
    const rows = Math.ceil(count / PER_ROW);
    for (let r = 0; r < rows; r++) {
      const n = Math.min(PER_ROW, count - r * PER_ROW);
      const center = ((PER_ROW - n) * cell) / 2;
      const stagger = n === PER_ROW ? (r % 2 === 0 ? -0.25 : 0.25) * cell : 0;
      for (let i = 0; i < n; i++) {
        out.push({
          x: -halfW + cell * (i + 0.5) + center + stagger + rng.range(-ROW_JITTER_X, ROW_JITTER_X),
          y: this.pathY + ROW_START + r * ROW_GAP + rng.range(-ROW_JITTER_Y, ROW_JITTER_Y),
        });
      }
    }
    return out;
  }

  private spawnTree(ctx: SimContext, p: Vec2, table: readonly SpawnEntry[]): void {
    const entry = pickSpawnEntry(ctx.rng.spawn, table, ctx.data.run.stageValue);
    if (!entry) return;
    spawnTarget(ctx, ctx.index.target(entry.targetId), p.x, p.y, { stageValue: ctx.data.run.stageValue });
    this.spawnedCount++;
  }

  /** Recentre le chemin (et les chats) pour garder des coordonnees petites ; invisible a l'ecran (sol periodique). */
  private rebase(ctx: SimContext): void {
    if (this.pathY < REBASE_AT) return;
    const dy = Math.floor(this.pathY / REBASE_STEP) * REBASE_STEP;
    this.pathY -= dy;
    this.bandEndY -= dy;
    for (const e of ctx.rt.world.all()) {
      e.transform.y -= dy;
      e.transform.prevY -= dy;
      if (e.kind === 'target') ctx.rt.targetGrid.update(e.id, e.transform.x, e.transform.y);
    }
  }

  /** Table du biome courant, poids des arbres rares (rare/epic) multiplies par la stat rareTreeChance (copie, config intacte). */
  private spawnTable(ctx: SimContext): SpawnEntry[] {
    const mult = ctx.stats.getNumber('rareTreeChance');
    return ctx.config.biomes[ctx.rt.biome].treeTable.map((e) => {
      const tier = ctx.index.target(e.targetId).rarity.tier;
      return tier === 'rare' || tier === 'epic' ? { ...e, weight: e.weight * mult } : e;
    });
  }

  /** Pose les arbres du niveau (une seule fois : ils ne repoussent pas). */
  protected override onStart(ctx: SimContext): void {
    const value = ctx.data.run.stageValue;
    const bal = ctx.config.balance;
    const boss = isBossLevel(bal, value);
    this.kills = 0;
    this.spawnedCount = 0;
    this.bossKilled = false;
    const table = this.spawnTable(ctx);
    const slots = this.layoutRows(ctx, levelTreeCount(bal, value));
    for (const p of slots) this.spawnTree(ctx, p, table);
    this.bandEndY = Math.max(this.pathY + ROW_START, ...slots.map((s) => s.y));
    if (boss) {
      this.bandEndY += BOSS_GAP;
      spawnTarget(ctx, ctx.index.target('tree_boss'), 0, this.bandEndY, {
        stageValue: value,
        hpMult: bal.stage.bossHpMult,
        rewardMult: bal.stage.bossRewardMult,
        isBoss: true,
      });
      this.spawnedCount++;
    }
    ctx.bus.emit('LevelStarted', { stageValue: value, boss, timed: true });
  }


  protected override onWin(ctx: SimContext): void {
    const bal = ctx.config.balance;
    const run = ctx.data.run;
    const value = run.stageValue;
    const boss = isBossLevel(bal, value);
    const dia = new Decimal(boss ? bal.stage.diaPerBossClear : bal.stage.diaPerLevelClear).times(ctx.stats.get('diaGain')).floor();
    const reward = { wallet: { dia } };
    ctx.tx.grantBundle(reward, 'levelClear');
    ctx.bus.emit('LevelCleared', { stageValue: value, boss, reward });
    ctx.services.missions.increment('stageClears', 1);
    run.stageValue = value + 1;
    run.bestStageValue = Math.max(run.bestStageValue, run.stageValue);
    ctx.data.progress.bestStageValue = Math.max(ctx.data.progress.bestStageValue, run.stageValue);
    const biome = biomeForStageValue(bal, run.stageValue, BIOME_ORDER);
    const biomeChanged = biome !== ctx.rt.biome;
    ctx.rt.biome = biome;
    // Les chats continuent d'avancer sur le chemin : le prochain troncon commence ou finissait la bande d'arbres.
    this.pathY = this.bandEndY;
    ctx.bus.emit('StageChanged', { stageValue: run.stageValue, biomeChanged });
  }

  /** Chrono ecoule : le niveau sera rejoue (nouvelles rangees devant les chats, la ou ils se trouvent). */
  protected override onLose(ctx: SimContext): void {
    const run = ctx.data.run;
    ctx.bus.emit('LevelFailed', { stageValue: run.stageValue });
    run.stageValue = Math.max(0, run.stageValue - this.params.failFallbackLevels);
    const leader = ctx.services.team.leader();
    if (leader) this.pathY = Math.max(this.pathY, leader.transform.y);
  }

  /** Fin du bandeau (Stage Clear / Temps ecoule) : arene videe, intro du niveau suivant ou du meme niveau (sans quitter le mode). */
  protected override onResult(ctx: SimContext): void {
    this.clearArena(ctx);
    this.rebase(ctx);
    ctx.rt.biome = biomeForStageValue(ctx.config.balance, ctx.data.run.stageValue, BIOME_ORDER);
    this.resetTimer(ctx);
    this.beginIntro(ctx);
  }

  /** Plus de phase farm / challenge (un echec rejoue simplement le niveau) : commandes conservees mais sans effet. */
  override handleCommand(_ctx: SimContext, _cmd: ModeCommand): Result {
    return okVoid;
  }

  protected override hudExtras(ctx: SimContext): Partial<ModeHud> {
    const run = ctx.data.run;
    const bal = ctx.config.balance;
    const d = stageValueToDisplay(bal, run.stageValue);
    const expected = levelTreeCount(bal, run.stageValue) + (isBossLevel(bal, run.stageValue) ? 1 : 0);
    let boss: ModeHud['boss'] = null;
    for (const e of ctx.rt.world.ofKind('target')) {
      if (e.target?.isBoss && e.health && e.visual?.dying === 0) {
        boss = { hp: e.health.hp, max: e.health.max };
        break;
      }
    }
    return {
      titleKey: 'hud.stage',
      titleParams: { stage: d.stage, level: d.level },
      progress: { current: Math.min(this.kills, expected), max: expected },
      boss,
      canChallenge: false,
    };
  }
}
